/*
 * Quantity stepper on product cards (collections, search, cart page, rails).
 *
 * Once a product is in the cart, the card's "Add to cart" is covered by a
 * - [N] + stepper bound to that cart line. The number is an input that also
 * takes typed values; going down to 0 removes the line and the button shows
 * again.
 *
 * - The stepper is rendered by Liquid (snippets/card-cart-stepper.liquid) for
 *   products already in the cart, so it is there on first paint after a
 *   reload. This script handles the interaction and adds or removes the
 *   stepper when the cart changes on the page.
 * - It reacts instantly: Add to cart shows the stepper on click, +/- and typed
 *   values update the number at once. The cart requests run behind it (+/-
 *   clicks settle for CHANGE_DELAY, then every settled change on the page goes
 *   out together through assets/cart-queue.js, see toSend) and the confirmed
 *   cart state wins once it is back.
 * - State comes from /cart.js after every cart:update event, so the stepper
 *   follows changes made anywhere (card, drawer, cart page).
 * - Changes are announced with the theme's own CartUpdateEvent, so the cart
 *   drawer and the header bubble refresh like after a normal add.
 * - + is marked disabled at the stock limit (data-max from Liquid, plus any
 *   cap the cart response reveals): aria-disabled, not disabled, so hovering
 *   or clicking it shows "Only N in stock" (assets/stock-limit-hint.js).
 * - The header cart bubble is optimistic too: it is set from here on every
 *   Add to cart click (any product form, warranty included) and every +/-,
 *   and corrected from the cart once the requests are back. The theme's
 *   cart-icon.js no longer adds to it on product form events.
 * - The stepper lives inside <add-to-cart-component>, before the button.
 *   The product page's own Add to cart (PDP_BUTTONS) turns into it the same
 *   way, for the selected variant, and so does the floating Add to cart bar
 *   (STICKY_ACTION, renderSticky).
 * - Which buttons count as a card's main Add to cart is decided here
 *   (CARD_BUTTONS) and, for the server-rendered markup, by CSS in
 *   snippets/product-card.liquid. Keep the two in sync.
 */
import { CartUpdateEvent, ThemeEvents } from '@theme/events';
// Cart requests go through one queue (assets/cart-queue.js).
import { enqueue, cartFetch, onLeave } from '@theme/cart-queue';

const SOURCE = 'card-cart-stepper';
const ROOT = window.Shopify?.routes?.root ?? '/';
const CARD_BUTTONS = 'product-card .buy-buttons-block add-to-cart-component, .bsr-card add-to-cart-component';
const STICKY_ACTION = '.sticky-add-to-cart__action';
// The product page's Add to cart (and the quick-add dialog's, same form).
// CSS in blocks/buy-buttons.liquid; keep in sync.
const PDP_BUTTONS = '.product-form-buttons add-to-cart-component';
const STEPPER_HOSTS = `${CARD_BUTTONS}, ${PDP_BUTTONS}`;
const CHANGE_DELAY = 350;

const strings = (() => {
  try {
    return JSON.parse(document.getElementById('card-cart-stepper-strings')?.textContent ?? '{}');
  } catch {
    return {};
  }
})();

/**
 * Per variant: the cart line the stepper edits, that line's quantity, and the
 * variant's total across all lines (shown on the card). Null until the cart
 * has been read once; until then the Liquid-rendered steppers are left as is.
 * @type {Map<string, {key: string, lineQuantity: number, total: number, plain: boolean}> | null}
 */
let lines = null;
/** The cart's items as last read (line item properties: warranties tied to their product). */
let cartItems = [];
/** item_count of the same cart snapshot as `lines`. */
let cartCount = null;

/** Variant id -> quantity shown before the cart confirms it. */
const optimistic = new Map();
/**
 * Variant id -> Add to cart clicks not yet in the cart: the variant total the
 * add leads to, and when it was clicked (a failed add is dropped on its error
 * event, anything else after ADD_TIMEOUT).
 * @type {Map<string, {target: number, at: number}>}
 */
const pendingAdds = new Map();
const ADD_TIMEOUT = 8000;
/** Variant id -> pending debounce timer. */
const timers = new Map();
/** Variant id -> stock cap revealed by a capped cart response. */
const learnedMax = new Map();
let inFlight = 0;

async function fetchCart() {
  const response = await fetch(`${ROOT}cart.js`, { headers: { Accept: 'application/json' } });
  if (!response.ok) throw new Error(`cart.js ${response.status}`);
  return response.json();
}

function indexCart(cart) {
  cartCount = cart.item_count ?? 0;
  cartItems = cart.items ?? [];
  const next = new Map();
  for (const item of cart.items ?? []) {
    const id = String(item.variant_id);
    const plain = !item.properties || Object.keys(item.properties).length === 0;
    const entry = next.get(id);
    if (!entry) {
      next.set(id, { key: item.key, lineQuantity: item.quantity, total: item.quantity, plain });
      continue;
    }
    entry.total += item.quantity;
    // Prefer editing a line without line item properties.
    if (!entry.plain && plain) {
      Object.assign(entry, { key: item.key, lineQuantity: item.quantity, plain: true });
    }
  }
  lines = next;
}

function variantIdOf(addToCart) {
  /** @type {HTMLInputElement | null} */
  const input = addToCart.closest('form')?.querySelector('input[name="id"]') ?? null;
  return input?.value || null;
}

/** A change of this variant is waiting or on its way. */
function isPending(variantId) {
  return optimistic.has(variantId) || timers.has(variantId) || toSend.has(variantId) || pendingAdds.has(variantId);
}

function shownQuantity(variantId) {
  if (optimistic.has(variantId)) return optimistic.get(variantId);
  return lines?.get(variantId)?.total ?? 0;
}

function maxOf(stepper, variantId) {
  const fromLiquid = !stepper || stepper.dataset.max === '' ? NaN : Number(stepper.dataset.max);
  const caps = [fromLiquid, learnedMax.get(variantId)].filter((value) => Number.isFinite(value) && value >= 0);
  return caps.length ? Math.min(...caps) : Infinity;
}

/**
 * Same markup as snippets/card-cart-stepper.liquid.
 *
 * The number is a <span role="spinbutton">, not an <input>: a new visible
 * input on the page makes the 1Password extension look for its label by
 * walking the whole document and measuring every element, which froze the
 * page for 2-2.5 s after the first Add to cart of a product with a few
 * pages of cards loaded (no hover, stale cursor; recorded in Brave on
 * 12. 10., data-1p-ignore did not stop it, CLIENT-155). A real input only
 * comes in while the customer types a quantity (startEditing).
 */
function createStepper(variantId, stockMax) {
  const stepper = document.createElement('div');
  stepper.className = 'card-cart-stepper';
  stepper.dataset.variantId = variantId;
  stepper.dataset.max = stockMax ?? '';
  stepper.innerHTML = `
    <button type="button" class="card-cart-stepper__button" data-step="-1"></button>
    <span class="card-cart-stepper__label" aria-hidden="true"></span>
    <span class="card-cart-stepper__input card-cart-stepper__value" role="spinbutton" tabindex="0" aria-valuemin="0"></span>
    <button type="button" class="card-cart-stepper__button" data-step="1"></button>
  `;
  const [minus, plus] = stepper.querySelectorAll('button');
  minus.textContent = '−';
  minus.setAttribute('aria-label', strings.decrease ?? 'Decrease quantity');
  plus.textContent = '+';
  plus.setAttribute('aria-label', strings.increase ?? 'Increase quantity');
  stepper.querySelector('.card-cart-stepper__value').setAttribute('aria-label', strings.quantity ?? 'Quantity');
  stepper.querySelector('.card-cart-stepper__label').textContent = strings.inCart ?? 'In your cart';
  return stepper;
}

/**
 * Typing a quantity: the number becomes a real input for as long as the
 * customer types (on a phone this opens the number keypad), and goes back
 * to the plain number once it is committed or left (stopEditing).
 * @param {HTMLElement} stepper
 * @param {string} [firstKey] a digit typed on the number, to start with
 */
function startEditing(stepper, firstKey) {
  const value = stepper.querySelector(':scope > .card-cart-stepper__value');
  if (!value) return;
  const variantId = stepper.dataset.variantId ?? '';
  const input = document.createElement('input');
  input.className = 'card-cart-stepper__input card-cart-stepper__field';
  input.type = 'number';
  input.inputMode = 'numeric';
  input.min = '0';
  input.step = '1';
  input.autocomplete = 'off';
  const max = maxOf(stepper, variantId);
  if (Number.isFinite(max)) input.max = String(max);
  input.value = firstKey ?? String(shownQuantity(variantId));
  input.setAttribute('aria-label', value.getAttribute('aria-label') ?? '');
  value.replaceWith(input);
  input.focus();
  if (firstKey === undefined) input.select();
}

/** Back to the plain number (after the change was handled). */
function stopEditing(input) {
  const stepper = input.closest('.card-cart-stepper');
  if (!stepper || !input.isConnected) return;
  const value = document.createElement('span');
  value.className = 'card-cart-stepper__input card-cart-stepper__value';
  value.setAttribute('role', 'spinbutton');
  value.tabIndex = 0;
  value.setAttribute('aria-valuemin', '0');
  value.setAttribute('aria-label', input.getAttribute('aria-label') ?? '');
  input.replaceWith(value);
  renderAll();
}

/**
 * The stock cap Liquid rendered, if it is for this variant: on the product
 * page the form switches variants without re-rendering the component.
 * @param {Element | null} addToCart
 * @param {string | null} variantId
 */
function stockMaxOf(addToCart, variantId) {
  const data = /** @type {HTMLElement | null} */ (addToCart)?.dataset;
  if (!data || !variantId) return undefined;
  return !data.stockVariant || data.stockVariant === variantId ? data.stockMax : undefined;
}

/**
 * Mark + at the stock limit (or not). See assets/stock-limit-hint.js.
 * @param {HTMLElement | null} plus
 * @param {boolean} atLimit
 * @param {number} max
 */
function setAtLimit(plus, atLimit, max) {
  if (!plus) return;
  if (!atLimit) {
    plus.removeAttribute('aria-disabled');
    delete plus.dataset.stockLimit;
    return;
  }
  plus.setAttribute('aria-disabled', 'true');
  plus.dataset.stockLimit = Number.isFinite(max) ? String(max) : '';
}

/**
 * A product page form with add-ons chosen next to Add to cart: an extended
 * warranty (for the selected variant, blocks/warranty-add-to-cart), Dwarf
 * boards or CHT nozzles (snippets/prusa-*-picker). Add to cart puts the
 * product in with what is chosen, as lines of their own (product-form.js).
 * The stepper over it does the same: + (or a higher number typed) goes
 * through that Add to cart, so a second printer gets the warranty picked
 * for it; - takes the product down and the extended warranties tied to it
 * with it, newest first, so there are never more warranties than printers.
 * @param {Element | null | undefined} addToCart
 */
function hasAddOns(addToCart) {
  if (!addToCart || addToCart.closest('product-card, .bsr-card')) return false;
  const form = addToCart.closest('product-form-component');
  const warranty = form?.querySelector('[data-warranty-wrapper]');
  if (warranty && !warranty.classList.contains('hidden') && warranty.querySelector('input[name="warranty-add-to-cart"]')) {
    return true;
  }
  return Boolean(form?.closest('.shopify-section')?.querySelector('.pdbp, .pcnp'));
}

/**
 * The product page's own <add-to-cart-component> a stepper belongs to: its
 * host, or for the floating bar's stepper the main form's.
 * @param {Element} stepper
 */
function pageAddToCartOf(stepper) {
  const host = stepper.parentElement;
  if (host?.matches(STICKY_ACTION)) {
    return (host.closest('.shopify-section') ?? document).querySelector('product-form-component add-to-cart-component');
  }
  return host;
}

/**
 * Variant id -> the extended warranty variant ids offered with it, for a
 * decrease made on a product page with add-ons (see hasAddOns): buildRequests
 * takes those warranties down with the product.
 * @type {Map<string, Set<string>>}
 */
const warrantiesOf = new Map();

/**
 * More of a product with add-ons: through its Add to cart (product-form.js),
 * `quantity` at once, with the add-ons picked for them.
 * @param {Element} addToCart
 * @param {number} quantity
 */
function addWithAddOns(addToCart, quantity) {
  const button = /** @type {HTMLButtonElement | null} */ (addToCart.querySelector('button[ref="addToCartButton"]'));
  if (!button || button.disabled) return;
  /** @type {HTMLInputElement | null} */
  const quantityInput = addToCart.closest('form')?.querySelector('input[name="quantity"]') ?? null;
  const shown = quantityInput?.value;
  if (quantityInput) quantityInput.value = String(quantity);
  // The click listener below counts it and the theme adds (the form is read
  // at once, so the field goes back right after).
  button.click();
  if (quantityInput && shown !== undefined) quantityInput.value = shown;
}

/**
 * Fewer of a product with add-ons: remember its warranties so the decrease
 * takes them down too.
 * @param {Element} addToCart
 * @param {string} variantId
 */
function noteWarranties(addToCart, variantId) {
  const ids = [...(addToCart.closest('form')?.querySelectorAll('input[name="warranty-add-to-cart"]') ?? [])]
    .map((input) => /** @type {HTMLInputElement} */ (input).value)
    .filter((value) => Number(value));
  if (ids.length) warrantiesOf.set(variantId, new Set(ids.map((id) => String(Number(id)))));
}

/**
 * The stepper's quantity, set by +/- or typing. On a product page with
 * add-ons an increase goes through Add to cart (addWithAddOns).
 * @param {HTMLElement} stepper
 * @param {number} value
 * @param {number} delay
 */
function changeQuantity(stepper, value, delay) {
  const variantId = stepper.dataset.variantId ?? '';
  const addToCart = pageAddToCartOf(stepper);
  if (variantId && addToCart && hasAddOns(addToCart)) {
    const shown = shownQuantity(variantId);
    const next = Math.min(maxOf(stepper, variantId), Math.max(0, value));
    if (next > shown) {
      addWithAddOns(addToCart, next - shown);
      return;
    }
    if (next < shown) noteWarranties(addToCart, variantId);
  }
  setQuantity(stepper, value, delay);
}

/** @param {Element} addToCart a card's (or the product page's) <add-to-cart-component> */
function render(addToCart) {
  const variantId = variantIdOf(addToCart);
  renderInto(addToCart, variantId, stockMaxOf(addToCart, variantId));
}

/**
 * The floating Add to cart bar on the product page: the same stepper over
 * its button, for the product (variant) selected in the page's main form.
 * Its button keeps adding through the main form (blocks/_sticky-add-to-cart).
 * @param {Element} action the bar's .sticky-add-to-cart__action
 */
function renderSticky(action) {
  const scope = action.closest('.shopify-section') ?? document;
  const addToCart = scope.querySelector('product-form-component add-to-cart-component');
  const variantId = addToCart ? variantIdOf(addToCart) : null;
  renderInto(action, variantId, stockMaxOf(addToCart, variantId));
}

/**
 * @param {Element} addToCart where the stepper goes, before its button
 * @param {string | null} variantId
 * @param {string | undefined} stockMax
 */
function renderInto(addToCart, variantId, stockMax) {
  /** @type {HTMLElement | null} */
  let stepper = addToCart.querySelector(':scope > .card-cart-stepper');

  // Before the first cart read, trust what Liquid rendered.
  if (lines === null && !optimistic.has(variantId ?? '')) return;

  const quantity = variantId ? shownQuantity(variantId) : 0;
  if (!variantId || quantity <= 0) {
    if (stepper) {
      stepper.remove();
      // The button shows again: not with the "Added" state of the click that
      // put it under the stepper a moment ago (the theme keeps it for 2 s).
      addToCart.querySelector('button[ref="addToCartButton"]')?.classList.remove('atc-added');
    }
    return;
  }

  // A variant switch on the card (swatches) changes the form's variant.
  if (stepper && stepper.dataset.variantId !== variantId) {
    stepper.remove();
    stepper = null;
  }
  if (!stepper) {
    stepper = createStepper(variantId, stockMax);
    // Before the button: CSS hides it with `.card-cart-stepper ~ button`.
    addToCart.prepend(stepper);
  }

  const max = maxOf(stepper, variantId);
  setAtLimit(/** @type {HTMLElement} */ (stepper.querySelector('[data-step="1"]')), quantity >= max, max);
  // The number (not while the customer types one, see startEditing). Only
  // the number changes: no animation (a scale "pop" grew it over - and +
  // and the product page's "In your cart" label).
  const value = stepper.querySelector(':scope > .card-cart-stepper__value');
  if (value) {
    if (value.textContent !== String(quantity)) value.textContent = String(quantity);
    value.setAttribute('aria-valuenow', String(quantity));
    if (Number.isFinite(max)) value.setAttribute('aria-valuemax', String(max));
    else value.removeAttribute('aria-valuemax');
  }
}

/**
 * Header bubble: the confirmed count plus what the customer clicked since.
 * Only touched while something is pending, or right after a fresh cart read
 * (`confirmed`), so it never overrides the theme with a stale snapshot.
 */
function renderBubble(confirmed = false) {
  if (cartCount === null || !lines || (!confirmed && optimistic.size === 0)) return;
  let count = cartCount;
  for (const [variantId, quantity] of optimistic) count += quantity - (lines.get(variantId)?.total ?? 0);
  count = Math.max(0, count);
  for (const icon of document.querySelectorAll('cart-icon')) {
    const current = Number.parseInt(icon.querySelector('[ref="cartBubbleCount"]')?.textContent ?? '', 10) || 0;
    if (current === count || typeof icon.renderCartBubble !== 'function') continue;
    icon.renderCartBubble(count, false, count > current);
  }
}

function renderAll() {
  document.querySelectorAll(STEPPER_HOSTS).forEach(render);
  document.querySelectorAll(STICKY_ACTION).forEach(renderSticky);
  renderBubble();
}

async function refresh() {
  try {
    indexCart(await fetchCart());
    // Adds that have landed (or failed silently) stop counting.
    for (const [variantId, add] of pendingAdds) {
      if ((lines?.get(variantId)?.total ?? 0) < add.target && Date.now() - add.at < ADD_TIMEOUT) continue;
      pendingAdds.delete(variantId);
      if (optimistic.get(variantId) === add.target) optimistic.delete(variantId);
    }
    // While a request of ours is still running, keep showing what the
    // customer clicked; the response settles it.
    if (inFlight === 0 && timers.size === 0) {
      for (const variantId of optimistic.keys()) if (!pendingAdds.has(variantId)) optimistic.delete(variantId);
    }
    renderAll();
    renderBubble(true);
  } catch (error) {
    console.error(error);
  }
}

/** Show the new quantity at once (clamped to 0..stock) and send it after `delay`. */
function setQuantity(stepper, value, delay) {
  const variantId = stepper.dataset.variantId;
  if (!variantId) return;
  const next = Math.min(maxOf(stepper, variantId), Math.max(0, value));
  optimistic.set(variantId, next);
  renderAll();

  clearTimeout(timers.get(variantId));
  timers.set(
    variantId,
    setTimeout(() => commit(variantId), delay)
  );
}

/**
 * Variant id -> quantity the customer settled on (debounce over), waiting for
 * the queue. Everything collected by the time the queue gets to it goes out
 * together, however many cards were changed:
 * - decreases in one /cart/update.js (by line key),
 * - increases in one /cart/add.js (the difference, by variant id).
 * Not everything through /cart/update.js: it does not check stock (tested
 * 2026-10-07: it accepted 5000 of a variant with 80 available), while
 * /cart/add.js caps an item at the stock, answers 422 and still adds the
 * other items of the request.
 */
const toSend = new Map();
let flushQueued = false;

function sectionIdsOnPage() {
  return [
    ...new Set(
      [...document.querySelectorAll('cart-items-component[data-section-id]')].map(
        (element) => /** @type {HTMLElement} */ (element).dataset.sectionId
      )
    ),
  ];
}

/**
 * The requests for these targets, from the current cart (line keys change
 * as the cart changes, so this runs right before sending). The stepper shows
 * the variant total: a decrease comes off the stepper's own line, other
 * lines of the same variant (with properties) keep their quantity.
 * @param {Map<string, number>} targets
 */
function buildRequests(targets) {
  /** @type {Record<string, number>} */
  const updates = {};
  /** @type {Array<{id: number, quantity: number}>} */
  const items = [];
  /** @type {Map<string, number>} */
  const sent = new Map();
  for (const [variantId, target] of targets) {
    const line = lines?.get(variantId);
    const total = line?.total ?? 0;
    if (target === total) {
      sent.set(variantId, target);
      continue;
    }
    if (target > total) {
      items.push({ id: Number(variantId), quantity: target - total });
    } else {
      if (!line?.key) continue;
      updates[line.key] = Math.max(0, line.lineQuantity - (total - target));
      trimWarranties(variantId, target, updates);
    }
    sent.set(variantId, target);
  }
  return { updates, items, sent };
}

/**
 * A product with add-ons going down to `target`: its extended warranties
 * (lines tied to it by main_variant_id, see warrantiesOf) down to `target`
 * too, the newest first (the cart lists the newest line first).
 * @param {string} variantId
 * @param {number} target
 * @param {Record<string, number>} updates
 */
function trimWarranties(variantId, target, updates) {
  const ids = warrantiesOf.get(variantId);
  if (!ids) return;
  warrantiesOf.delete(variantId);
  const tied = cartItems.filter(
    (item) => ids.has(String(item.variant_id)) && String(item.properties?.main_variant_id ?? '') === variantId
  );
  let excess = tied.reduce((sum, item) => sum + item.quantity, 0) - target;
  for (const item of tied) {
    if (excess <= 0) break;
    const off = Math.min(item.quantity, excess);
    updates[item.key] = item.quantity - off;
    excess -= off;
  }
}

/** Hand the settled quantity of one variant to the next batch. */
async function commit(variantId, attempt = 0) {
  timers.delete(variantId);
  const target = optimistic.get(variantId);
  if (target === undefined) return;

  if (lines === null) await refresh();
  if (!lines?.get(variantId)?.key) {
    // +/- used while the first Add to cart is still on its way: wait for the
    // line to exist (cart:update refreshes `lines`), then send.
    if (pendingAdds.has(variantId) && attempt < 20) {
      timers.set(
        variantId,
        setTimeout(() => commit(variantId, attempt + 1), CHANGE_DELAY)
      );
      return;
    }
    // Not in the cart and nothing on its way: an increase goes out as an
    // add (below); zero is already true.
    if (target === 0 || pendingAdds.has(variantId)) {
      optimistic.delete(variantId);
      return refresh();
    }
  }

  toSend.set(variantId, target);
  if (flushQueued) return;
  flushQueued = true;
  inFlight += 1;
  enqueue(sendBatch).finally(() => {
    inFlight -= 1;
  });
}

async function sendBatch() {
  // Changes settled from here on go into the next batch.
  flushQueued = false;
  const batch = new Map(toSend);
  toSend.clear();
  if (!batch.size) return;

  const sectionIds = sectionIdsOnPage();
  const post = (endpoint, payload, withSections) =>
    cartFetch(`${ROOT}cart/${endpoint}.js`, {
      method: 'POST',
      keepalive: true,
      headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
      body: JSON.stringify({
        ...payload,
        ...(withSections && sectionIds.length
          ? { sections: sectionIds.join(','), sections_url: window.location.pathname }
          : {}),
      }),
    });

  const settle = () => {
    for (const [variantId, target] of batch) {
      if (optimistic.get(variantId) === target) optimistic.delete(variantId);
    }
  };

  try {
    let { updates, items, sent } = buildRequests(batch);
    let sections = null;
    let capped = false;

    if (Object.keys(updates).length) {
      let response = await post('update', { updates }, !items.length);
      // A line key that went stale anyway (cart changed elsewhere): read the
      // cart and send once more.
      if (response.status === 400) {
        indexCart(await fetchCart());
        ({ updates, items, sent } = buildRequests(batch));
        response = await post('update', { updates }, !items.length);
      }
      if (!response.ok) throw new Error(`cart/update.js ${response.status}`);
      const data = await response.json().catch(() => null);
      if (data?.sections) sections = data.sections;
    }

    if (items.length) {
      const response = await post('add', { items }, true);
      const data = await response.json().catch(() => null);
      // 422: over the stock. Shopify still adds what is available and the
      // other items; the cart read below shows how many.
      if (!response.ok && response.status !== 422) throw new Error(data?.description ?? `cart/add.js ${response.status}`);
      if (response.ok && data?.sections) sections = data.sections;
      capped = response.status === 422;
    }

    const current = await fetchCart();
    indexCart(current);
    for (const [variantId, target] of sent) {
      const confirmed = lines?.get(variantId)?.total ?? 0;
      // Remember the cap so + stays disabled.
      if (confirmed < target) learnedMax.set(variantId, confirmed);
    }
    // Clicks made while this request ran stay shown; their own batch follows.
    settle();
    renderAll();
    renderBubble(true);
    document.dispatchEvent(
      new CartUpdateEvent(current, SOURCE, {
        source: SOURCE,
        itemCount: current.item_count,
        variantIds: [...sent.keys()],
        ...(sections && !capped ? { sections } : {}),
      })
    );
    // cart-icon.js just set the confirmed count; keep other pending clicks in it.
    renderBubble();
  } catch (error) {
    console.error(error);
    settle();
    await refresh();
  }
}

// Leaving the page with +/- still waiting (debounce, or not yet sent): send
// them right away (assets/cart-queue.js sends with keepalive).
onLeave(() => {
  for (const [variantId, timer] of timers) {
    clearTimeout(timer);
    const target = optimistic.get(variantId);
    if (target !== undefined && lines?.get(variantId)?.key) toSend.set(variantId, target);
  }
  timers.clear();
  if (!toSend.size) return [];
  const { updates, items } = buildRequests(toSend);
  toSend.clear();
  return [
    ...(Object.keys(updates).length ? [{ url: `${ROOT}cart/update.js`, body: { updates } }] : []),
    ...(items.length ? [{ url: `${ROOT}cart/add.js`, body: { items } }] : []),
  ];
});

/** The stepper an event happened in, if it belongs to a card's main button. */
function stepperOf(event) {
  const stepper = event.target instanceof Element ? event.target.closest('.card-cart-stepper') : null;
  if (!stepper) return null;
  const addToCart = stepper.parentElement;
  return addToCart?.matches(STEPPER_HOSTS) || addToCart?.matches(STICKY_ACTION)
    ? /** @type {HTMLElement} */ (stepper)
    : null;
}

// Listeners are delegated in the capture phase, so they cover the
// Liquid-rendered steppers too and run before any card handler (nothing
// inside the stepper may open the product page).
document.addEventListener(
  'click',
  (event) => {
    const stepper = stepperOf(event);
    if (stepper) {
      event.stopPropagation();
      if (event.target instanceof Element && event.target.closest('.card-cart-stepper__value')) {
        event.preventDefault();
        startEditing(stepper);
        return;
      }
      const button = /** @type {HTMLButtonElement | null} */ (
        event.target instanceof Element ? event.target.closest('.card-cart-stepper__button') : null
      );
      if (!button || button.disabled || button.getAttribute('aria-disabled') === 'true') return;
      event.preventDefault();
      changeQuantity(stepper, shownQuantity(stepper.dataset.variantId ?? '') + Number(button.dataset.step), CHANGE_DELAY);
      return;
    }

    // Add to cart (cards, product page, quick add): count it at once, the
    // theme adds. On a card this also shows the stepper right away.
    const button = event.target instanceof Element ? event.target.closest('button[ref="addToCartButton"]') : null;
    const addToCart = button?.closest('add-to-cart-component');
    if (!button || !addToCart || button.disabled) return;
    const form = addToCart.closest('form');
    const variantId = variantIdOf(addToCart);
    if (!variantId || !form) return;

    // A card's button showing again after - went to 0 while that change is
    // still on its way (or the line still in the cart): Add to cart means
    // "one in the cart", set through the stepper's own batch. Letting the
    // theme add on top would count the line that is about to be removed.
    /** @type {HTMLInputElement | null} */
    const quantityInput = form.querySelector('input[name="quantity"]');
    if (
      addToCart.matches(STEPPER_HOSTS) &&
      !hasAddOns(addToCart) &&
      (isPending(variantId) || (lines?.get(variantId)?.total ?? 0) > 0)
    ) {
      event.preventDefault();
      event.stopImmediatePropagation();
      const stepper =
        addToCart.querySelector(':scope > .card-cart-stepper') ??
        createStepper(variantId, stockMaxOf(addToCart, variantId));
      if (!stepper.isConnected) addToCart.prepend(stepper);
      // The product page's quantity field counts; a card adds one.
      setQuantity(stepper, Math.max(1, Number.parseInt(quantityInput?.value ?? '', 10) || 1), 0);
      return;
    }
    const adds = [[variantId, Math.max(1, Number.parseInt(quantityInput?.value ?? '', 10) || 1)]];
    // The extended warranty is added right after the product (product-form.js).
    /** @type {HTMLInputElement | null} */
    const warranty = form.querySelector('input[name="warranty-add-to-cart"]:checked');
    if (warranty?.value && Number(warranty.value)) adds.push([String(Number(warranty.value)), adds[0][1]]);
    for (const [id, quantity] of adds) {
      const target = shownQuantity(id) + quantity;
      optimistic.set(id, target);
      pendingAdds.set(id, { target, at: Date.now() });
    }
    // Render after the theme's own click handler has started the add.
    queueMicrotask(renderAll);
  },
  true
);

document.addEventListener(
  'change',
  (event) => {
    const stepper = stepperOf(event);
    if (!stepper) return;
    event.stopPropagation();
    const input = /** @type {HTMLInputElement} */ (event.target);
    const typed = Number.parseInt(input.value, 10);
    if (!Number.isFinite(typed)) {
      input.value = String(shownQuantity(stepper.dataset.variantId ?? ''));
      return;
    }
    changeQuantity(stepper, typed, 0);
  },
  true
);

document.addEventListener(
  'keydown',
  (event) => {
    const stepper = stepperOf(event);
    if (!stepper || !(event.target instanceof Element)) return;
    // On the plain number (keyboard focus): Enter or a digit starts typing,
    // arrows step like + and -.
    if (event.target.matches('.card-cart-stepper__value')) {
      const variantId = stepper.dataset.variantId ?? '';
      if (event.key === 'Enter' || /^[0-9]$/.test(event.key)) {
        event.preventDefault();
        startEditing(stepper, event.key === 'Enter' ? undefined : event.key);
      } else if (event.key === 'ArrowUp' || event.key === 'ArrowDown') {
        event.preventDefault();
        changeQuantity(stepper, shownQuantity(variantId) + (event.key === 'ArrowUp' ? 1 : -1), CHANGE_DELAY);
      }
      return;
    }
    if (event.key === 'Escape' && event.target.matches('.card-cart-stepper__field')) {
      event.preventDefault();
      // Leave without the typed value: put the shown one back first.
      /** @type {HTMLInputElement} */ (event.target).value = String(shownQuantity(stepper.dataset.variantId ?? ''));
      /** @type {HTMLElement} */ (event.target).blur();
      return;
    }
    if (event.key !== 'Enter') return;
    event.preventDefault();
    /** @type {HTMLElement} */ (event.target).blur();
  },
  true
);

// Typing done (the change event, if any, has been handled): back to the number.
document.addEventListener(
  'focusout',
  (event) => {
    if (!(event.target instanceof HTMLInputElement) || !event.target.matches('.card-cart-stepper__field')) return;
    const input = event.target;
    // After the change listener (change fires before focusout) and any
    // re-render it started.
    setTimeout(() => stopEditing(input));
  },
  true
);

// Product page variant switch: the stepper follows the selected variant
// (product-form.js switches the form's variant id at once, see
// #onVariantSelected; the server's update follows).
document.addEventListener(ThemeEvents.variantSelected, () => queueMicrotask(renderAll));
document.addEventListener(ThemeEvents.variantUpdate, () => queueMicrotask(renderAll));

document.addEventListener(ThemeEvents.cartUpdate, (event) => {
  // Our own updates already carry the fresh cart.
  if (event.detail?.data?.source === SOURCE) return;
  // A refused add (stock, sold out): stop counting the clicks at once.
  if (event.detail?.data?.didError) {
    for (const [variantId, add] of pendingAdds) {
      if (optimistic.get(variantId) === add.target) optimistic.delete(variantId);
    }
    pendingAdds.clear();
  }
  refresh();
});

// Filters, sorting, pagination and recommendations insert cards later, and
// the theme re-renders (morphs) a card after Add to cart, which can drop a
// stepper the script added. Only those changes matter: cards or Add to cart
// buttons added, or a stepper removed. Everything else on the page
// (announcement bar, slideshows, drawer) is ignored, so this stays cheap.
const touches = (nodes, selector) =>
  [...nodes].some((node) => node instanceof Element && (node.matches(selector) || node.querySelector(selector)));

let queued = false;
new MutationObserver((mutations) => {
  if (queued) return;
  const relevant = mutations.some(
    (mutation) =>
      touches(mutation.addedNodes, 'add-to-cart-component') || touches(mutation.removedNodes, '.card-cart-stepper')
  );
  if (!relevant) return;
  queued = true;
  setTimeout(() => {
    queued = false;
    renderAll();
  });
}).observe(document.body, { childList: true, subtree: true });

refresh();

// The cart response re-renders (morphs) the header with the server's count,
// which does not know about clicks still queued. While any are pending, put
// the optimistic count back; the observer runs before the next paint, so the
// lower number is never shown.
const bubbleObserver = new MutationObserver(() => {
  if (optimistic.size > 0) renderBubble();
});
for (const icon of document.querySelectorAll('cart-icon')) {
  bubbleObserver.observe(icon, { subtree: true, childList: true, characterData: true });
}
